import React from 'react'

import {
  Box,
  HStack,
  BoxProps,
  VStack,
  useDisclosure,
  Spinner,
  Center,
  Flex
} from '@chakra-ui/react'
import { PrimaryButton, SecondaryButton } from 'components/Buttons'
import {
  Agent,
  useGetAgencyAgentsQuery,
  Enum_Agent_Status,
  useGetMyUserQuery
} from 'generated/graphql'
import AgentCard from 'components/Property/Admin/Listings/AgentCard'
import { FilterOptions, SmSearchBar, SortOptions } from 'components/Property'
import { SearchByType, StatusFilterType } from 'types'
import { VscClose } from 'react-icons/vsc'
import { useFormikContext } from 'formik'
import { AddPropertyValues } from '.'
import { EmptyHandler } from 'components'

type PropertyAgentManagerProps = {
  agent?: Agent
  onEdit?: () => void
  onReplace?: (id?: string) => void
} & BoxProps
const PropertyAgentManager: React.FC<PropertyAgentManagerProps> = ({
  agent,
  onReplace,
  ...rest
}) => {
  const { data: me } = useGetMyUserQuery()
  const { isOpen: isReplaceOpen, onToggle: onReplaceToggle } = useDisclosure()
  const [searchTerm, setSearchTerm] = React.useState('')
  const [searchByActive, setSearchByActive] = React.useState<SearchByType>('name')
  const [activeSort, setActiveSort] = React.useState('created_at:desc')
  const [filter, setFilter] = React.useState<StatusFilterType>('all')
  const formikContext = useFormikContext<AddPropertyValues>()

  React.useEffect(() => {
    if (!formikContext?.values?.agent) {
      formikContext?.setFieldValue('agent', me?.getMyUser?.agent as Agent)
    }
  }, [me?.getMyUser?.agent])

  const where = {
    id_ne: agent?.id,
    status: Enum_Agent_Status.Active,
    ...(searchByActive === 'name' && { name_contains: searchTerm || undefined }),
    ...(searchByActive === 'email' && { email_contains: searchTerm || undefined }),
    ...(searchByActive === 'phone_number' && { phone_number_contains: searchTerm || undefined }),
    ...(filter !== 'all' && { 'user.role.type': filter })
  }

  const { data: agents, loading } = useGetAgencyAgentsQuery({
    variables: {
      where,
      sort: activeSort,
      limit: 5
    }
  })
  const selectedAgentFromForm = formikContext?.values?.agent

  const [selectedAgent, setSelectedAgent] = React.useState<Agent | null>(
    () => selectedAgentFromForm || null
  )

  return (
    <Flex p={8} direction="column" pl={10} pr={4} flex={0.8} {...rest}>
      {onReplace ? (
        <AgentCard agent={agent as Agent} isMobile />
      ) : (
        selectedAgentFromForm && <AgentCard agent={selectedAgentFromForm} isMobile />
      )}
      {me?.getMyUser?.role?.type === 'admin' && (
        <HStack spacing={4}>
          <PrimaryButton
            leftIcon={isReplaceOpen ? <VscClose /> : undefined}
            colorScheme={isReplaceOpen ? 'red' : 'blue'}
            onClick={() => {
              onReplaceToggle()
            }}
          >
            {isReplaceOpen ? 'Cancel' : 'Replace'}
          </PrimaryButton>
        </HStack>
      )}
      {isReplaceOpen ? (
        <Box pt={8}>
          <SmSearchBar
            placeholder={`Search by ${searchByActive}`}
            setSearchBy={setSearchByActive}
            options={[
              { value: 'name', label: 'Name' },
              { value: 'email', label: 'Email' },
              { value: 'phone_number', label: 'Phone Number' }
            ]}
            onSearch={(text) => {
              setSearchTerm(text)
            }}
          />
          <VStack alignItems="flex-start">
            <SortOptions
              options={[
                { value: 'created_at:desc', label: 'Newest' },
                { value: 'created_at:asc', label: 'Oldest' }
              ]}
              activeSort={activeSort}
              setActiveSort={setActiveSort}
            />
            <FilterOptions
              filter={filter}
              options={[
                {
                  label: 'All',
                  value: 'all'
                },
                {
                  label: 'Admin',
                  value: 'admin'
                },
                {
                  label: 'Agent',
                  value: 'agent'
                }
              ]}
              setActiveFilter={setFilter}
            />
          </VStack>
          <VStack pr={1} spacing={4} py={8} mt={1}>
            {loading ? (
              <Center>
                <Spinner />
              </Center>
            ) : agents?.getAgencyAgents?.length === 0 ? (
              <EmptyHandler />
            ) : (
              (agents?.getAgencyAgents?.map((agent) => (
                <Flex w="100%" key={agent?.id}>
                  <AgentCard
                    agent={agent as Agent}
                    p={4}
                    onClick={() => {
                      setSelectedAgent(agent as Agent)
                    }}
                    isFocused={agent?.id === selectedAgent?.id}
                  />
                  {agent?.id === selectedAgent?.id && (
                    <VStack spacing={4} ml={4}>
                      <PrimaryButton
                        onClick={() => {
                          if (onReplace) {
                            onReplace?.(selectedAgent?.id || '')
                          } else {
                            formikContext?.setFieldValue('agent', selectedAgent as Agent)
                          }
                          onReplaceToggle()
                        }}
                      >
                        Confirm
                      </PrimaryButton>
                      <SecondaryButton onClick={onReplaceToggle}>Cancel</SecondaryButton>
                    </VStack>
                  )}
                </Flex>
              )) as any)
            )}
          </VStack>
        </Box>
      ) : (
        <></>
      )}
    </Flex>
  )
}

export default PropertyAgentManager
